import { useState, useEffect } from 'react';
import {
  Video,
  PersonStanding,
  Wrench,
  MessageCircle,
  BookOpen,
  Settings,
  LogOut,
  X,
} from 'lucide-react';
import { useNavigate } from 'react-router';
import { useUiStore } from '../../core/stores/uiStore.js';
import { logout } from '../../core/auth/session.js';
import { Button } from '../ui/Button.jsx';
import { sidebarNavItems, sidebarFooterItems, isNavItemVisible } from './navConfig.js';

function LogoutConfirm({ onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
      <div className="w-full max-w-sm rounded-[var(--radius-card)] bg-white p-6 shadow-[var(--shadow-lg)]">
        <p className="mb-6 text-center text-lg text-brand">Are you sure you want to logout?</p>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={onCancel} className="flex-1">
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm} className="flex-1">
            Log Out
          </Button>
        </div>
      </div>
    </div>
  );
}

export function MenuDrawer() {
  const isOpen = useUiStore((s) => s.isMenuOpen);
  const closeMenu = useUiStore((s) => s.closeMenu);
  const navigate = useNavigate();
  const [confirmLogout, setConfirmLogout] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen && !confirmLogout) return null;

  const visibleItems = sidebarNavItems.filter(isNavItemVisible);

  const handleNavigate = (item) => {
    closeMenu();
    if (item.videoType) {
      navigate(item.path, { state: { yocasType: item.videoType } });
      return;
    }
    navigate(item.path);
  };

  const handleLogout = async () => {
    setConfirmLogout(false);
    closeMenu();
    await logout();
    navigate('/welcome');
  };

  const renderNavItem = (item, onClick) => {
    const Icon = item.icon;
    return (
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-3 rounded-xl px-3 py-3.5 text-left text-brand transition-colors hover:bg-brand/5 active:bg-brand/10 min-h-[44px]"
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand/8">
          <Icon className="h-5 w-5 text-brand" strokeWidth={2} />
        </span>
        <span className="text-base font-medium">{item.label}</span>
      </button>
    );
  };

  return (
    <div className="md:hidden">
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-[60] bg-black/30 backdrop-blur-[2px]"
            onClick={closeMenu}
            role="presentation"
            aria-hidden="true"
          />

          <aside
            className="menu-drawer fixed top-0 left-0 z-[61] flex h-full w-[75%] max-w-xs flex-col overflow-y-auto border-r border-brand/5 bg-gradient-to-br from-surface via-white/95 to-accent-peach/10 p-4 shadow-[var(--shadow-lg)]"
            aria-label="Main menu"
          >
            <button
              type="button"
              onClick={closeMenu}
              className="mb-4 flex h-10 w-10 items-center justify-center rounded-full text-brand transition-colors hover:bg-brand/5 min-h-0 min-w-0"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>

            <nav className="flex flex-1 flex-col gap-1">
              {visibleItems.map((item) => (
                <div key={item.label}>
                  {renderNavItem(item, () => handleNavigate(item))}
                </div>
              ))}
            </nav>

            <div className="mt-auto flex flex-col gap-1 border-t border-brand/10 pt-4 pb-8">
              {renderNavItem(
                { label: 'Settings', icon: Settings },
                () => {
                  closeMenu();
                  navigate('/settings');
                }
              )}
              {renderNavItem(
                { label: 'Log Out', icon: LogOut },
                () => {
                  closeMenu();
                  setConfirmLogout(true);
                }
              )}
            </div>
          </aside>
        </>
      )}

      {confirmLogout && (
        <LogoutConfirm
          onConfirm={handleLogout}
          onCancel={() => setConfirmLogout(false)}
        />
      )}
    </div>
  );
}
